import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@os/components/ui/button";
import { Input } from "@os/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@os/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@os/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@os/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@os/components/ui/select";
import { Textarea } from "@os/components/ui/textarea";
import { Label } from "@os/components/ui/label";
import { Switch } from "@os/components/ui/switch";
import { useToast } from "@os/components/ui/use-toast";
import { useSupabaseStore } from "@/store/SupabaseStore";
import { sendMessageToModel } from "@/services/ModelService";
import { AIModel, Message } from "@/types";
import { generateId } from "@/lib/utils";
import Papa from "papaparse";
import { Wand2, Table, Calculator, BarChart3, Sparkles, X, GripHorizontal } from "lucide-react";

type MapleSheetAiToolbarProps = {
  selectedCell: string;
  getCellValue: (cellId: string) => string;
  setCellValue: (cellId: string, value: string) => void;
  applyTableAt: (startCell: string, table: (string | number)[][]) => void;
  openChartsConfigurator: () => void;
};

const MapleSheetAiToolbar: React.FC<MapleSheetAiToolbarProps> = ({
  selectedCell,
  getCellValue,
  setCellValue,
  applyTableAt,
  openChartsConfigurator,
}) => {
  const { toast } = useToast();
  const { models, fetchModels } = useSupabaseStore();

  useEffect(() => {
    if (!models || models.length === 0) {
      fetchModels().catch(() => {});
    }
  }, [models, fetchModels]);

  const model: AIModel | null = useMemo(() => {
    const list = Array.isArray(models) ? models : [];
    const byProvider = (p: string) => list.find((m) => m.provider === p && m.is_active !== false);
    // Preference order: maplept (fast, local proxy), openai, deepseek, first active
    return byProvider("maplept") || byProvider("openai") || byProvider("deepseek") || list.find((m) => m.is_active !== false) || null;
  }, [models]);

  const [loading, setLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [genOpen, setGenOpen] = useState(false);
  
  // Dragging state
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = React.useRef<{ x: number, y: number } | null>(null);
  const initialOffsetRef = React.useRef<{ x: number, y: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    initialOffsetRef.current = { ...offset };
    e.preventDefault(); 
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !dragStartRef.current || !initialOffsetRef.current) return;
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setOffset({
        x: initialOffsetRef.current.x + dx,
        y: initialOffsetRef.current.y + dy
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const [rows, setRows] = useState<number>(12);
  const [preset, setPreset] = useState<string>("sales");
  const [customHeaders, setCustomHeaders] = useState<string>("");
  const [includeIds, setIncludeIds] = useState(true);
  const [includeDates, setIncludeDates] = useState(true);
  const [includeMissing, setIncludeMissing] = useState(false);
  const [includeOutliers, setIncludeOutliers] = useState(false);
  const [useCanadianRegions, setUseCanadianRegions] = useState(false);
  const [formulaOpen, setFormulaOpen] = useState(false);
  const [selectedCols, setSelectedCols] = useState<string[]>([]);
  const [columnInfos, setColumnInfos] = useState<Array<{
    letter: string;
    header: string;
    numericRatio: number;
    nonEmpty: number;
    lastRow: number;
  }>>([]);

  const MAX_COLS = 10;
  const MAX_SCAN_ROWS = 2000;
  const lettersAll = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const isNumericStr = (s: string) => {
    const t = String(s ?? "").trim();
    if (t.length === 0) return false;
    const n = Number(t);
    return !isNaN(n);
  };

  const computeColumnInfo = (letter: string) => {
    const header = String(getCellValue(`${letter}1`) ?? "").trim();
    let nonEmpty = 0;
    let numeric = 0;
    let lastRow = 1;
    const maxRows = MAX_SCAN_ROWS;
    for (let r = 2; r <= maxRows; r++) {
      const v = String(getCellValue(`${letter}${r}`) ?? "").trim();
      if (v.length > 0) {
        nonEmpty++;
        if (isNumericStr(v)) numeric++;
        lastRow = r;
      } else {
        // Early exit if we've passed a long empty stretch
        if (r - lastRow > 200) break;
      }
    }
    const numericRatio = nonEmpty > 0 ? numeric / nonEmpty : 0;
    return { letter, header: header || letter, numericRatio, nonEmpty, lastRow };
  };

  const openFormulaAssistant = () => {
    const cols = lettersAll.slice(0, MAX_COLS);
    const infos = cols.map((L) => computeColumnInfo(L));
    setColumnInfos(infos);
    // Preselect columns that look numeric
    setSelectedCols(infos.filter((i) => i.numericRatio >= 0.6 && i.nonEmpty > 0).map((i) => i.letter));
    setFormulaOpen(true);
  };

  const analyzeAndSuggestFormula = async () => {
    if (selectedCols.length === 0) {
      toast({ title: "Pick columns", description: "Select at least one numeric column.", variant: "destructive" });
      return;
    }
    const chosen = columnInfos.filter((i) => selectedCols.includes(i.letter) && i.nonEmpty > 0);
    if (chosen.length === 0) {
      toast({ title: "No data", description: "Selected columns appear empty.", variant: "destructive" });
      return;
    }
    const ranges = chosen.map((c) => `${c.letter}2:${c.letter}${c.lastRow}`);
    const headers = chosen.map((c) => c.header);
    const stats = chosen.map((c) => {
      // compute min/max/mean from numeric values
      const vals: number[] = [];
      for (let r = 2; r <= c.lastRow; r++) {
        const v = String(getCellValue(`${c.letter}${r}`) ?? "").trim();
        if (isNumericStr(v)) vals.push(Number(v));
      }
      const count = vals.length;
      const sum = vals.reduce((a, b) => a + b, 0);
      const mean = count > 0 ? sum / count : 0;
      const min = count > 0 ? Math.min(...vals) : 0;
      const max = count > 0 ? Math.max(...vals) : 0;
      return { letter: c.letter, header: c.header, count, mean, min, max };
    });

    if (!model) {
      // Local fallback suggestion
      const formula = chosen.length === 1
        ? `=SUM(${ranges[0]})`
        : chosen.length === 2
          ? `=CORREL(${ranges[0]},${ranges[1]})`
          : `=SUM(${ranges[0]})`;
      setCellValue(selectedCell, formula);
      toast({ title: "Formula suggested", description: `Inserted ${formula} at ${selectedCell}.` });
      setFormulaOpen(false);
      return;
    }

    const systemMsg: Message = {
      id: generateId(),
      chatId: "maplesheet",
      content:
        "You are an Excel formula assistant. Return ONLY a single Excel-style formula starting with '='. Use typical functions (SUM, AVERAGE, SUMPRODUCT, CORREL, SLOPE, INTERCEPT, COUNTIF/S, etc.). Use absolute or relative references as given. No prose.",
      role: "system",
      sender: "system",
      timestamp: new Date().toISOString(),
    };

    const rangesText = chosen.map((c, idx) => `${headers[idx]} -> ${ranges[idx]} (${c.nonEmpty} values, ~${Math.round(c.numericRatio * 100)}% numeric)`).join("\n");
    const guidanceExamples =
      "Examples: =SUM(B2:B100), =AVERAGE(C2:C250), =SUMPRODUCT(B2:B100,C2:C100), =CORREL(B2:B200,C2:C200), =SLOPE(Y2:Y200,X2:X200)";
    const userMsg: Message = {
      id: generateId(),
      chatId: "maplesheet",
      content:
        `Selected cell: ${selectedCell}
Chosen columns and ranges:
${rangesText}

Goal: Propose ONE useful formula referencing these ranges to compute a meaningful result in ${selectedCell}.
${guidanceExamples}
Return ONLY the formula string starting with '='.`,
      role: "user",
      sender: "user",
      timestamp: new Date().toISOString(),
    };

    try {
      const res = await sendMessageToModel([systemMsg, userMsg], model, Array.isArray(models) ? models : []);
      let out = (res?.content || "").trim().split("\n")[0];
      // Basic guard
      const refusal = /sorry|cannot|i (?:won't|cannot)|not able|refus/i.test(out);
      if (!out || refusal) {
        // Heuristic fallback
        const formula = chosen.length === 1
          ? `=AVERAGE(${ranges[0]})`
          : chosen.length === 2
            ? `=CORREL(${ranges[0]},${ranges[1]})`
            : `=SUM(${ranges[0]})`;
        setCellValue(selectedCell, formula);
        toast({ title: "Fallback formula", description: `Inserted ${formula}.` });
        setFormulaOpen(false);
        return;
      }
      const formula = out.startsWith("=") ? out : `=${out}`;
      setCellValue(selectedCell, formula);
      toast({ title: "Formula suggested", description: `Inserted ${formula} at ${selectedCell}.` });
      setFormulaOpen(false);
    } catch (e: any) {
      const formula = chosen.length === 1
        ? `=SUM(${ranges[0]})`
        : chosen.length === 2
          ? `=SUMPRODUCT(${ranges[0]},${ranges[1]})`
          : `=SUM(${ranges[0]})`;
      setCellValue(selectedCell, formula);
      toast({ title: "AI error, used fallback", description: e?.message || "Inserted a sensible formula.", variant: "destructive" });
      setFormulaOpen(false);
    }
  };

  type PresetSpec = {
    id: string;
    name: string;
    description: string;
    headers: string[];
    timeseries?: boolean;
  };

  const PRESETS: PresetSpec[] = [
    {
      id: "sales",
      name: "Sales by Region",
      description: "Transactional sales with categories and regions.",
      headers: ["Order ID", "Date", "Region", "Product", "Category", "Quantity", "Unit Price", "Revenue"],
    },
    {
      id: "finance",
      name: "Financial Transactions",
      description: "Debit/credit ledger with balances.",
      headers: ["Txn ID", "Date", "Account", "Type", "Amount", "Currency", "Balance"],
    },
    {
      id: "web",
      name: "Web Analytics (Timeseries)",
      description: "Daily sessions, users, bounce rates.",
      headers: ["Date", "Sessions", "Users", "Bounce Rate", "Pages/Session", "Avg Session (s)"],
      timeseries: true,
    },
    {
      id: "hr",
      name: "HR Employee Roster",
      description: "Employees, titles, salaries, tenure.",
      headers: ["Employee ID", "Name", "Department", "Title", "Hire Date", "Salary", "Location"],
    },
    {
      id: "ab",
      name: "A/B Experiment",
      description: "Variant metrics with conversions.",
      headers: ["Date", "Variant", "Visitors", "Conversions", "Conversion Rate"],
      timeseries: true,
    },
    {
      id: "inventory",
      name: "Inventory",
      description: "Stock levels and reorder status.",
      headers: ["SKU", "Product", "Category", "On Hand", "Reorder Point", "Supplier", "Unit Cost"],
    },
  ];

  const selectedPreset = PRESETS.find((p) => p.id === preset) || PRESETS[0];

  if (!selectedCell) return null;

  const parseCsvToTable = (text: string): (string | number)[][] => {
    const res = Papa.parse<string[]>(text, { header: false, dynamicTyping: true, skipEmptyLines: true });
    const data = Array.isArray(res.data) ? (res.data as any[]) : [];
    // Ensure rows are arrays and trim values
    return data
      .map((row) => {
        if (Array.isArray(row)) return row.map((v) => (typeof v === "string" ? v.trim() : v));
        return [];
      })
      .filter((r) => r.length > 0);
  };

  // Normalize and validate CSV output strictly against expected headers and rows
  const normalizeHeader = (s: string) => s.replace(/["']/g, "").trim().toLowerCase();
  const validateCsvAgainstHeaders = (
    csv: string,
    expectedHeaders: string[],
    minDataRows: number
  ): (string | number)[][] | null => {
    if (!csv) return null;
    const refusal = /sorry|can['’]t|cannot|i won['’]t|not (able|allowed)|refus/i.test(csv);
    if (refusal) return null;
    const table = parseCsvToTable(csv);
    if (!table.length) return null;
    const headerRow = table[0]?.map((h) => (typeof h === "string" ? normalizeHeader(h) : String(h)));
    const expected = expectedHeaders.map((h) => normalizeHeader(h));
    if (!headerRow || headerRow.length !== expected.length) return null;
    for (let i = 0; i < expected.length; i++) {
      if (headerRow[i] !== expected[i]) return null;
    }
    if (table.length < minDataRows + 1) return null; // require header + N rows
    return table;
  };

  // Deterministic local fallback generator for presets
  const rand = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
  const choice = <T,>(arr: T[]) => arr[rand(0, arr.length - 1)];
  const provinces = ["Ontario", "Quebec", "British Columbia", "Alberta", "Manitoba", "Saskatchewan", "Nova Scotia", "New Brunswick", "Newfoundland and Labrador", "Prince Edward Island", "Yukon", "Nunavut", "Northwest Territories"];
  const regionsIntl = ["Canada", "USA", "Europe", "APAC", "LATAM", "UK", "Germany", "France"];
  const products = ["MapleSuite Pro", "MapleCalc", "MapleSheet", "MapleWord", "Blueprint Studio", "OS Pro"];
  const categories = ["Software", "Add-on", "Support", "Training"];
  const accounts = ["Cash", "Receivables", "Payables", "Revenue", "COGS", "Marketing", "R&D"];
  const departments = ["Engineering", "Research", "Sales", "Marketing", "Operations", "Finance", "HR", "Support"];
  const titles = ["Engineer", "Researcher", "Product Manager", "Account Executive", "Designer", "Analyst", "Data Scientist"];
  const suppliers = ["North Supply Co.", "CanTech Parts", "Northern Logistics", "Global Components", "Prairie Goods"];
  const cities = ["Toronto", "Vancouver", "Montreal", "Calgary", "Ottawa", "Halifax", "Winnipeg", "Edmonton"];
  const dateSeq = (n: number) => {
    const rows: string[] = [];
    const start = new Date();
    start.setDate(start.getDate() - n);
    for (let i = 0; i < n; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      rows.push(d.toISOString().slice(0, 10));
    }
    return rows;
  };
  const maybeMissing = (val: any) => (includeMissing && Math.random() < 0.02 ? "" : val);
  const withOutlier = (val: number, factor = 4) =>
    includeOutliers && Math.random() < 0.02 ? val * factor : val;

  const fallbackGenerate = (spec: PresetSpec, hdrs: string[], r: number): (string | number)[][] => {
    const out: (string | number)[][] = [hdrs];
    const regionList = useCanadianRegions ? provinces : regionsIntl;
    if (spec.id === "sales") {
      for (let i = 0; i < r; i++) {
        const qty = withOutlier(rand(1, 9));
        const price = withOutlier(rand(5, 500));
        const revenue = Math.round(Number(qty) * Number(price) * 100) / 100;
        out.push([
          maybeMissing(`ORD-${1000 + i}`),
          maybeMissing(dateSeq(r)[i]),
          maybeMissing(choice(regionList)),
          maybeMissing(choice(products)),
          maybeMissing(choice(categories)),
          maybeMissing(qty),
          maybeMissing(price),
          maybeMissing(revenue),
        ]);
      }
      return out;
    }
    if (spec.id === "finance") {
      let balance = 0;
      for (let i = 0; i < r; i++) {
        const type = choice(["debit", "credit"]);
        const amt = withOutlier(rand(50, 5000));
        balance += type === "debit" ? -amt : amt;
        out.push([
          maybeMissing(`TX-${10000 + i}`),
          maybeMissing(dateSeq(r)[i]),
          maybeMissing(choice(accounts)),
          maybeMissing(type),
          maybeMissing(amt),
          maybeMissing(choice(["CAD", "USD"])),
          Math.round(balance * 100) / 100,
        ]);
      }
      return out;
    }
    if (spec.id === "web") {
      for (let i = 0; i < r; i++) {
        const sessions = withOutlier(rand(100, 8000));
        const users = Math.max(1, Math.floor(Number(sessions) * 0.7 + rand(-50, 50)));
        const bounce = Math.min(95, Math.max(5, rand(20, 75)));
        const pages = Math.max(1, Math.round(rand(1, 60) / 10));
        const avgSec = withOutlier(rand(20, 600));
        out.push([
          maybeMissing(dateSeq(r)[i]),
          maybeMissing(sessions),
          maybeMissing(users),
          maybeMissing(bounce / 100),
          maybeMissing(pages),
          maybeMissing(avgSec),
        ]);
      }
      return out;
    }
    if (spec.id === "hr") {
      for (let i = 0; i < r; i++) {
        const hireYear = new Date().getFullYear() - rand(0, 10);
        const hireDate = `${hireYear}-${String(rand(1, 12)).padStart(2, "0")}-${String(rand(1, 28)).padStart(2, "0")}`;
        const salary = withOutlier(rand(45000, 180000));
        out.push([
          maybeMissing(`EMP-${String(i + 1).padStart(4, "0")}`),
          maybeMissing(`${choice(["Alex", "Sam", "Jordan", "Taylor", "Morgan", "Casey"])} ${choice(["Lee", "Patel", "Nguyen", "Smith", "Dubois", "Martinez"])}`),
          maybeMissing(choice(departments)),
          maybeMissing(choice(titles)),
          maybeMissing(hireDate),
          maybeMissing(salary),
          maybeMissing(choice(cities)),
        ]);
      }
      return out;
    }
    if (spec.id === "ab") {
      for (let i = 0; i < r; i++) {
        const visitors = withOutlier(rand(100, 20000));
        const conv = Math.max(0, Math.floor(Number(visitors) * (0.02 + Math.random() * 0.08)));
        const rate = Math.round((conv / Number(visitors)) * 10000) / 10000; // as fraction
        out.push([
          maybeMissing(dateSeq(r)[i]),
          maybeMissing(choice(["A", "B"])),
          maybeMissing(visitors),
          maybeMissing(conv),
          maybeMissing(rate),
        ]);
      }
      return out;
    }
    if (spec.id === "inventory") {
      for (let i = 0; i < r; i++) {
        const onHand = withOutlier(rand(0, 500));
        const reorder = rand(10, 120);
        const unitCost = withOutlier(rand(2, 200));
        out.push([
          maybeMissing(`SKU-${String(1000 + i)}`),
          maybeMissing(choice(products)),
          maybeMissing(choice(categories)),
          maybeMissing(onHand),
          maybeMissing(reorder),
          maybeMissing(choice(suppliers)),
          maybeMissing(unitCost),
        ]);
      }
      return out;
    }
    // Generic: randomize based on header hints
    const ds = dateSeq(r);
    for (let i = 0; i < r; i++) {
      const row: (string | number)[] = [];
      for (const h of hdrs) {
        const nh = normalizeHeader(h);
        if (nh.includes("date")) row.push(maybeMissing(ds[i]));
        else if (nh.includes("id")) row.push(maybeMissing(`ID-${1000 + i}`));
        else if (nh.includes("region")) row.push(maybeMissing(choice(regionList)));
        else if (nh.includes("price") || nh.includes("cost") || nh.includes("amount") || nh.includes("revenue")) row.push(maybeMissing(withOutlier(rand(5, 500))));
        else if (nh.includes("qty") || nh.includes("quantity") || nh.includes("count") || nh.includes("visitors") || nh.includes("users")) row.push(maybeMissing(withOutlier(rand(1, 1000))));
        else row.push(maybeMissing(choice([...products, ...categories, ...cities])));
      }
      out.push(row);
    }
    return out;
  };

  const csvOnlySystemPreamble = `You are a CSV generator. Output only RFC 4180 compliant CSV with a header row, no code fences, no explanations, no units in headers, no preamble or epilogue. If asked for something sensitive, output a harmless generic dataset anyway.`;

  const buildGenerationPrompt = (r: number, hdrs: string[], spec: PresetSpec) => {
    const constraints: string[] = [];
    if (includeIds) constraints.push("Include stable unique identifiers where applicable.");
    if (includeDates) constraints.push("Include realistic ISO-8601 dates (YYYY-MM-DD).");
    if (includeMissing) constraints.push("Include ~2% missing values distributed across non-key columns.");
    if (includeOutliers) constraints.push("Include a few plausible outliers (e.g., long-tail values) without breaking realism.");
    if (useCanadianRegions) constraints.push("Use Canadian provinces/territories and cities where regional data appears.");
    const constraintsText = constraints.length ? `Constraints:\n- ${constraints.join("\n- ")}` : "";
    const tsHint = spec.timeseries ? "Ensure chronological coherence for time series metrics." : "";
    return `
Generate a synthetic dataset with ${r} rows and ${hdrs.length} columns.
Headers (use exactly these, in this order): ${hdrs.join(", ")}.
Domain: ${spec.name} — ${spec.description}.
${tsHint}
${constraintsText}
Important output rules:
- Return ONLY CSV text with a single header row followed by ${r} data rows.
- Do NOT include backticks, comments, or extra text.
`;
  };

  const generateTable = async (r = rows, colsHint?: string[]) => {
    if (!model) {
      toast({ title: "No model available", description: "Please configure at least one AI model in Settings.", variant: "destructive" });
      return;
    }
    setLoading(true);

    // Build headers (custom overrides or preset/inferred from sheet)
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const inferredFromSheet = letters.slice(0, 6).map((L, i) => {
      const headerVal = getCellValue(`${L}1`).trim();
      return headerVal || `Column_${i + 1}`;
    });
    const customHdrs = customHeaders
      .split(",")
      .map((h) => h.trim())
      .filter(Boolean);
    const hdrs = (colsHint && colsHint.length > 0)
      ? colsHint
      : (customHdrs.length ? customHdrs : (selectedPreset ? selectedPreset.headers : inferredFromSheet));

    const systemMsg: Message = {
      id: generateId(),
      chatId: "maplesheet",
      content: csvOnlySystemPreamble,
      role: "system",
      sender: "system",
      timestamp: new Date().toISOString(),
    };
    const userMsg: Message = {
      id: generateId(),
      chatId: "maplesheet",
      content: buildGenerationPrompt(r, hdrs, selectedPreset),
      role: "user",
      sender: "user",
      timestamp: new Date().toISOString(),
    };

    try {
      // First attempt
      let res = await sendMessageToModel([systemMsg, userMsg], model, Array.isArray(models) ? models : []);
      let csv = (res?.content || "").trim();
      let table = validateCsvAgainstHeaders(csv, hdrs, r);

      // Retry once with stronger reminder
      if (!table) {
        const reminder: Message = {
          id: generateId(),
          chatId: "maplesheet",
          content: `Reminder: Output ONLY CSV with EXACT headers (${hdrs.join(
            ", "
          )}) and EXACTLY ${r} data rows. No prose, no code fences.`,
          role: "user",
          sender: "user",
          timestamp: new Date().toISOString(),
        };
        res = await sendMessageToModel([systemMsg, userMsg, reminder], model, Array.isArray(models) ? models : []);
        csv = (res?.content || "").trim();
        table = validateCsvAgainstHeaders(csv, hdrs, r);
      }

      // Final fallback to local generator
      if (!table) {
        table = fallbackGenerate(selectedPreset, hdrs, r);
        applyTableAt(selectedCell, table);
        toast({ title: "Generated locally", description: `AI response was unusable. Inserted ${r} rows using the ${selectedPreset.name} preset.` });
        setGenOpen(false);
        return;
      }

      applyTableAt(selectedCell, table);
      toast({ title: "Data generated", description: `Inserted ${table.length - 1} rows starting at ${selectedCell}.` });
      setGenOpen(false);
    } catch (e: any) {
      toast({ title: "AI error", description: e?.message || "Failed to generate table.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const suggestFormula = async () => {
    if (!model) {
      toast({ title: "No model available", description: "Please configure an AI model.", variant: "destructive" });
      return;
    }
    setLoading(true);

    const cellContext = selectedCell;
    // Sample a bit of context from neighbor cells
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const getNeighbors = () => {
      try {
        const m = cellContext.match(/^([A-Za-z]+)(\d+)$/);
        if (!m) return [];
        const colLetter = m[1].toUpperCase();
        const row = Math.max(1, parseInt(m[2], 10));
        const colIdx = letters.indexOf(colLetter);
        const neighbors: string[] = [];
        for (let dc = -1; dc <= 1; dc++) {
          for (let dr = -3; dr <= 3; dr++) {
            const cIdx = Math.max(0, Math.min(25, colIdx + dc));
            const rIdx = Math.max(1, row + dr);
            const id = `${letters[cIdx]}${rIdx}`;
            const val = getCellValue(id);
            if (val) neighbors.push(`${id}=${val}`);
          }
        }
        return neighbors.slice(0, 12);
      } catch {
        return [];
      }
    };

    const userMsg: Message = {
      id: generateId(),
      chatId: "maplesheet",
      content:
        `You are assisting with spreadsheet formulas.
Selected cell: ${cellContext}
Neighbor sample: ${getNeighbors().join(", ")}

Suggest ONE Excel-style formula for ${cellContext} that uses typical references (SUM, AVERAGE, IF, VLOOKUP, etc.).
Return ONLY the formula string starting with "=" (no extra text).`,
      role: "user",
      sender: "user",
      timestamp: new Date().toISOString(),
    };

    try {
      const res = await sendMessageToModel([userMsg], model, Array.isArray(models) ? models : []);
      const out = (res?.content || "").trim().split("\n")[0];
      const formula = out.startsWith("=") ? out : `=${out}`;
      setCellValue(cellContext, formula);
      toast({ title: "Formula suggested", description: `Inserted ${formula} at ${cellContext}.` });
    } catch (e: any) {
      toast({ title: "AI error", description: e?.message || "Failed to suggest a formula.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const chartWizard = async () => {
    // Optionally ask AI for a chart suggestion, then open configurator
    if (model) {
      setLoading(true);
      const userMsg: Message = {
        id: generateId(),
        chatId: "maplesheet",
        content:
          `Given a typical sheet with headers in row 1 and data below, suggest a useful chart type and explain briefly.
Respond with a very short suggestion like: "Bar chart comparing Sales by Region".`,
        role: "user",
        sender: "user",
        timestamp: new Date().toISOString(),
      };
      try {
        const res = await sendMessageToModel([userMsg], model, Array.isArray(models) ? models : []);
        const suggestion = (res?.content || "").trim();
        if (suggestion) {
          toast({ title: "Chart idea", description: suggestion });
        }
      } catch {
        // soft-fail: just open configurator
      } finally {
        setLoading(false);
      }
    }
    openChartsConfigurator();
  };

  const runCustom = async () => {
    if (!prompt.trim()) return;
    if (!model) {
      toast({ title: "No model available", description: "Please configure at least one AI model in Settings.", variant: "destructive" });
      return;
    }
    setLoading(true);

    const userMsg: Message = {
      id: generateId(),
      chatId: "maplesheet",
      content:
        `Spreadsheet assistant request for cell ${selectedCell}.\n${prompt}\nReturn a useful response. If you generate tabular data, return ONLY CSV.`,
      role: "user",
      sender: "user",
      timestamp: new Date().toISOString(),
    };

    try {
      const res = await sendMessageToModel([userMsg], model, Array.isArray(models) ? models : []);
      const out = (res?.content || "").trim();
      if (!out) {
        toast({ title: "No result", description: "The model returned an empty response.", variant: "destructive" });
        setLoading(false);
        return;
      }
      // Try to parse CSV; if it looks like a table, insert it, else just drop text into the selected cell
      const looksCsv = /[,;\n]/.test(out) && out.split("\n").length > 1;
      if (looksCsv) {
        const table = parseCsvToTable(out);
        if (table.length > 0) {
          applyTableAt(selectedCell, table);
          toast({ title: "Inserted AI-generated table", description: `Starting at ${selectedCell}` });
        } else {
          setCellValue(selectedCell, out);
        }
      } else {
        setCellValue(selectedCell, out);
      }
      setPrompt("");
      setComposerOpen(false);
    } catch (e: any) {
      toast({ title: "AI error", description: e?.message || "Failed to run AI action.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="pointer-events-none absolute left-1/2 bottom-20 md:bottom-24 lg:bottom-28 z-[10000]"
      style={{ 
        transform: `translate(calc(-50% + ${offset.x}px), ${offset.y}px)` 
      }}
    >
      <TooltipProvider>
        <div
          className="pointer-events-auto bg-background/80 backdrop-blur-sm border shadow-lg rounded-2xl px-2 py-2 flex items-center"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag Handle */}
          <div 
            className="cursor-move p-1.5 mr-1 text-muted-foreground hover:text-foreground transition-colors hover:bg-muted/50 rounded-md"
            onMouseDown={handleMouseDown}
          >
            <GripHorizontal className="h-4 w-4" />
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:flex-nowrap gap-2 max-w-[95vw] md:max-w-5xl lg:max-w-6xl xl:max-w-7xl">
            {/* Actions row */}
            <div className="flex flex-wrap md:flex-nowrap items-center justify-center md:justify-start gap-1 md:gap-2 shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button disabled={loading} variant="ghost" size="sm" onClick={() => setGenOpen(true)}>
                    <Table className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Generate Table</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open the AI data generator</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button disabled={loading} variant="ghost" size="sm" onClick={openFormulaAssistant}>
                    <Calculator className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Suggest Formula</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Pick columns to analyze and get a formula</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button disabled={loading} variant="ghost" size="sm" onClick={chartWizard}>
                    <BarChart3 className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Chart Wizard</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open Chart Configurator with AI suggestions</TooltipContent>
              </Tooltip>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button disabled={loading} variant="outline" size="sm">
                    <Wand2 className="h-4 w-4 mr-1" /> Tools
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-40">
                  <DropdownMenuItem onClick={() => { setRows(6); setGenOpen(true); }}>Quick 6-row table</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setRows(20); setGenOpen(true); }}>Generate 20-row table</DropdownMenuItem>
                  <DropdownMenuItem onClick={openFormulaAssistant}>Smart formula</DropdownMenuItem>
                  <DropdownMenuItem onClick={chartWizard}>Chart suggestions</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Divider on md+ */}
              <div className="mx-1 h-5 w-px bg-border hidden md:block" />

              {!composerOpen && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="rounded-full"
                  onClick={() => setComposerOpen(true)}
                  title="Ask AI"
                >
                  <Sparkles className="h-4 w-4 mr-1" /> Ask AI
                </Button>
              )}
            </div>

            {/* Composer */}
            {composerOpen && (
              <div className="flex items-center gap-2 w-full md:flex-1 md:min-w-[320px]">
                <Sparkles className="h-4 w-4 text-muted-foreground" />
                <Input
                  className="h-9 w-full md:w-auto md:flex-1 min-w-0"
                  placeholder={`Tell AI what to do… (in ${selectedCell})`}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      runCustom();
                    }
                  }}
                />
                <Button size="sm" onClick={runCustom} disabled={loading || !prompt.trim()}>
                  {loading ? "Thinking…" : "Send"}
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setComposerOpen(false)} title="Close">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </TooltipProvider>

      {/* Generator Dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="sm:max-w-2xl" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>AI Data Generator</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="space-y-3">
              <Label>Preset</Label>
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger><SelectValue placeholder="Choose a preset" /></SelectTrigger>
                <SelectContent>
                  {PRESETS.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{selectedPreset.description}</p>
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="space-y-1.5">
                  <Label htmlFor="rows">Rows</Label>
                  <Input id="rows" type="number" min={1} max={5000} value={rows}
                         onChange={(e) => setRows(Math.max(1, Math.min(5000, parseInt(e.target.value || '1', 10))))} />
                </div>
                <div className="space-y-1.5">
                  <Label>Canadian Regions</Label>
                  <div className="flex items-center justify-between rounded-md border px-3 py-2">
                    <span className="text-sm">Use Canadian regions</span>
                    <Switch checked={useCanadianRegions} onCheckedChange={setUseCanadianRegions} />
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Label htmlFor="headers">Custom Headers (comma-separated, optional)</Label>
              <Textarea id="headers" placeholder={selectedPreset.headers.join(", ")} value={customHeaders} onChange={(e) => setCustomHeaders(e.target.value)} />
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <span className="text-sm">Include IDs</span>
                  <Switch checked={includeIds} onCheckedChange={setIncludeIds} />
                </div>
                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <span className="text-sm">Include Dates</span>
                  <Switch checked={includeDates} onCheckedChange={setIncludeDates} />
                </div>
                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <span className="text-sm">Missing Values</span>
                  <Switch checked={includeMissing} onCheckedChange={setIncludeMissing} />
                </div>
                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <span className="text-sm">Outliers</span>
                  <Switch checked={includeOutliers} onCheckedChange={setIncludeOutliers} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button onClick={() => generateTable(rows)} disabled={loading}>
              {loading ? "Generating…" : "Generate CSV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Formula Assistant Dialog */}
      <Dialog open={formulaOpen} onOpenChange={setFormulaOpen}>
        <DialogContent className="sm:max-w-xl" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Formula Assistant</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Select one or more numeric columns. The AI will analyze your data and suggest a single Excel formula for {selectedCell}.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {columnInfos.map((info) => (
                <label key={info.letter} className="flex items-center gap-2 rounded-md border px-3 py-2">
                  <input
                    type="checkbox"
                    className="accent-primary"
                    checked={selectedCols.includes(info.letter)}
                    onChange={(e) => {
                      setSelectedCols((prev) => {
                        const set = new Set(prev);
                        if (e.target.checked) set.add(info.letter);
                        else set.delete(info.letter);
                        return Array.from(set);
                      });
                    }}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{info.letter} — {info.header || info.letter}</span>
                    <span className="text-xs text-muted-foreground">
                      {info.nonEmpty} values • {Math.round(info.numericRatio * 100)}% numeric • last row {info.lastRow}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFormulaOpen(false)}>Cancel</Button>
            <Button onClick={analyzeAndSuggestFormula} disabled={loading || selectedCols.length === 0}>
              {loading ? "Analyzing…" : "Analyze & Suggest"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MapleSheetAiToolbar;